/**
 * État de session participant.
 *
 * Un seul appel au démarrage (/api/acces/moi) fournit tout : qui je suis,
 * l'événement, mes consentements, si mon profil est complet. Conservé en
 * localStorage pour que la coquille s'affiche instantanément hors ligne, puis
 * rafraîchi dès que le réseau répond.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { appelerApi } from './reseau.ts';

export type Moi = {
  participant_id: string;
  prenom: string | null;
  nom: string | null;
  rejoint_le: string | null;
  evenement_nom: string;
  evenement_slug: string;
  debut_le: string;
  fin_le: string;
  lieu_nom: string | null;
  lieu_ville: string | null;
  durees_creneau: number[];
  politique_affichage: string;
  profil_complete: boolean;
};

export type EtatSession = {
  moi: Moi | null;
  consentements: Record<string, boolean>;
  charge: boolean;
  recharger: () => Promise<void>;
  sortir: () => Promise<void>;
};

const Contexte = createContext<EtatSession>({
  moi: null,
  consentements: {},
  charge: false,
  recharger: async () => {},
  sortir: async () => {},
});

const CLE_CACHE = 'eventmatch.moi';

type ReponseAcces = { moi: Moi; consentements: { type: string; accorde: boolean }[] };

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [etat, setEtat] = useState<{ moi: Moi | null; consentements: Record<string, boolean> }>(
    () => {
      try {
        return JSON.parse(localStorage.getItem(CLE_CACHE) ?? 'null') ?? { moi: null, consentements: {} };
      } catch {
        return { moi: null, consentements: {} };
      }
    },
  );
  const [charge, setCharge] = useState(false);

  const recharger = useCallback(async () => {
    const reponse = await appelerApi<ReponseAcces>('/api/acces/moi');
    if (reponse.donnees) {
      const consentements = Object.fromEntries(
        reponse.donnees.consentements.map((c) => [c.type, c.accorde]),
      );
      const suivant = { moi: reponse.donnees.moi, consentements };
      setEtat(suivant);
      try {
        localStorage.setItem(CLE_CACHE, JSON.stringify(suivant));
      } catch {
        /* stockage indisponible : sans conséquence */
      }
    } else if (reponse.erreur && !reponse.depuisLeCache && reponse.erreur !== 'Connexion indisponible') {
      // Session réellement invalide (401) — pas une simple coupure réseau.
      setEtat({ moi: null, consentements: {} });
      localStorage.removeItem(CLE_CACHE);
    }
    setCharge(true);
  }, []);

  const sortir = useCallback(async () => {
    await appelerApi('/api/acces/sortir', { method: 'POST' });
    localStorage.removeItem(CLE_CACHE);
    setEtat({ moi: null, consentements: {} });
  }, []);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  // Heartbeat de présence : à l'ouverture puis toutes les deux minutes tant que
  // l'onglet est visible. C'est lui qui allume la pastille « sur place » des
  // autres participants.
  useEffect(() => {
    if (!etat.moi) return;
    const signaler = () => {
      if (document.visibilityState === 'visible') {
        void appelerApi('/api/repertoire/presence', { method: 'POST' });
      }
    };
    signaler();
    const intervalle = setInterval(signaler, 2 * 60_000);
    document.addEventListener('visibilitychange', signaler);
    return () => {
      clearInterval(intervalle);
      document.removeEventListener('visibilitychange', signaler);
    };
  }, [etat.moi]);

  const valeur = useMemo(
    () => ({ ...etat, charge, recharger, sortir }),
    [etat, charge, recharger, sortir],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): EtatSession {
  return useContext(Contexte);
}
